import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Info, TrendingUp, Shield, Users, ChevronRight, Download, BarChart2, Calendar } from 'lucide-react';
import './portfolio.css';
import { Link } from 'react-router-dom';
// Performance Data
const performanceData = [
  {
    year: '2022',
    kleos: 0,
    prometheus: -20,
    benchmark: -18
  },
  {
    year: '2023',
    kleos: 135,
    prometheus: 44,
    benchmark: 23
  },
  {
    year: '2024',
    kleos: 85,
    prometheus: 47,
    benchmark: 23
  },
  {
    year: '2025',
    kleos: 6,
    prometheus: 6,
    benchmark: 0.2
  }
  
];


// Risk Rating Component
const RiskRating = ({ level }) => {
  const levels = ['Low', 'Moderate', 'High'];
  const currentIndex = levels.indexOf(level);
  
  return (
    <div className="risk-rating">
      <div className="risk-bars">
        {levels.map((_, index) => (
          <div 
            key={index} 
            className={`risk-bar ${index <= currentIndex ? 'active' : ''}`}
          />
        ))}
      </div>
      <span className="risk-label">{level} Risk</span>
    </div>
  );
};

// Metric Component
const Metric = ({ label, value, prefix = '', suffix = '', trend = null }) => (
  <div className="metric-item">
    <p className="metric-label">{label}</p>
    <div className={`metric-value ${trend !== null ? (trend >= 0 ? 'positive' : 'negative') : ''}`}>
      {trend !== null && (
        trend >= 0 ? 
          <ArrowUpRight size={20} /> :
          <ArrowDownRight size={20} />
      )}
      {prefix}{value}{suffix}
    </div>
  </div>
);

// Strategy Item Component
const StrategyItem = ({ icon: Icon, title, description }) => (
  <div className="strategy-item">
    <Icon className="strategy-icon" />
    <div className="strategy-content">
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
  </div>
);

// Fund Card Component
const FundCard = ({ fund }) => {
  const { name, type, description, metrics, strategy, riskProfile } = fund;
  
  return (
    <div className="fund-card">
      <div className="fund-header">
        <h3 className="fund-title">{name}</h3>
        <span className={`fund-type ${type.toLowerCase().replace(' ', '-')}`}>
          {type}
        </span>
      </div>
      
      <p className="fund-description">{description}</p>
      
      <div className="metrics-grid">
        <Metric 
          label="2024 Return"
          value={metrics.ytdReturn}
          suffix="%"
          trend={metrics.ytdReturn}
        />
        <Metric 
          label="AUM"
          value={metrics.aum}
          prefix="$"
          suffix="K"
        />
        <Metric 
          label="Sharpe Ratio"
          value={metrics.sharpeRatio.toFixed(2)}
        />
      </div>

      <div className="risk-section">
        <h4>Risk Level</h4>
        <RiskRating level={riskProfile.level} />
      </div>

      <div className="strategy-details">
        <StrategyItem 
          icon={TrendingUp}
          title="Investment Strategy"
          description={strategy.approach}
        />
        <StrategyItem 
          icon={Shield}
          title="Risk Management"
          description={riskProfile.description}
        />
        <StrategyItem 
          icon={Users}
          title="Ideal For"
          description={strategy.targetInvestor}
        />
      </div>

      <div className="fund-metrics">
        <div className="metric-row">
          <div className="metric">
            <Calendar className="metric-icon" />
            <div>
              <span className="metric-label">Min. Investment Period</span>
              <span className="metric-value">{metrics.minPeriod}</span>
            </div>
          </div>
          <div className="metric">
            <BarChart2 className="metric-icon" />
            <div>
              <span className="metric-label">Historical Volatility</span>
              <span className="metric-value">{metrics.volatility}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="fund-actions">
        <button className="action-button primary" onclick link to="/signup" >
        <Link to="/signup" className="action-button primary"> Invest Now </Link>
        <ChevronRight size={20} />
        </button>
        <button className="action-button secondary" title="Download Fund Report">
          <Download size={16} />
        </button>
      </div>
    </div>
  );
};

// Performance Chart Component
const PerformanceChart = () => (
  <div className="performance-chart">
    <div className="chart-header">
      <h2>Historical Performance</h2>
      <p>Long-term performance comparison since 2022</p>
    </div>
    
    <div className="chart-container">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={performanceData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
          <XAxis 
            dataKey="year" 
            tick={{ fill: '#94a3b8' }}
            axisLine={{ stroke: 'rgba(255, 255, 255, 0.1)' }}
          />
          <YAxis 
            tickFormatter={(value) => `${value}%`}
            tick={{ fill: '#94a3b8' }}
            axisLine={{ stroke: 'rgba(255, 255, 255, 0.1)' }}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: '#0f1115',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            labelStyle={{ color: '#ffffff' }}
            itemStyle={{ color: '#94a3b8' }}
            formatter={(value) => [`${value}%`]}
          />
          <Legend 
            wrapperStyle={{
              paddingTop: '20px',
              color: '#94a3b8'
            }}
          />
          <Line 
            type="monotone" 
            dataKey="kleos" 
            name="Kleos Fund" 
            stroke="#0066ff" 
            strokeWidth={2}
            dot={{ strokeWidth: 2 }}
            activeDot={{ r: 8 }}
          />
          <Line 
            type="monotone" 
            dataKey="prometheus" 
            name="Prometheus Fund" 
            stroke="#10b981" 
            strokeWidth={2}
            dot={{ strokeWidth: 2 }}
            activeDot={{ r: 8 }}
          />
          <Line 
            type="monotone" 
            dataKey="benchmark" 
            name="S&P 500" 
            stroke="#f59e0b" 
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ strokeWidth: 2 }}
            activeDot={{ r: 8 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
    
    <div className="chart-disclaimer">
      <Info size={20} />
      <p>Past performance does not guarantee future results. Historical data shown for comparative purposes only.</p>
    </div>
  </div>
);

// Main Portfolio Component
const Portfolio = () => {
  const funds = [
    {
      name: "Kleos Alpha Fund",
      type: "High Growth",
      description: "Advanced quantitative strategy leveraging AI and machine learning for exceptional returns in dynamic markets.",
      metrics: {
        ytdReturn: 87.2,
        aum: 50,
        sharpeRatio: 1.47,
        minPeriod: "6 Months",
        volatility: 35
      },
      strategy: {
        approach: "Advanced quantitative analysis and machine learning optimization",
        targetInvestor: "Growth-oriented investors comfortable with higher volatility for potentially higher returns"
      },
      riskProfile: {
        level: "High",
        description: "Actively managed portfolio using sophisticated risk management and hedging strategies"
      }
    },
    {
      name: "Prometheus Balanced Fund",
      type: "Moderate",
      description: "Strategic allocation across multiple asset classes for stable, risk-adjusted returns with focus on capital preservation.",
      metrics: {
        ytdReturn: 48.8,
        aum: 150,
        sharpeRatio: 1.32,
        minPeriod: "1 Year",
        volatility: 25
      },
      strategy: {
        approach: "Diversified multi-asset strategy with tactical adjustments based on market conditions",
        targetInvestor: "Balanced investors seeking steady growth with moderate risk tolerance"
      },
      riskProfile: {
        level: "Moderate",
        description: "Balanced risk-return profile with focus on downside protection and steady growth"
      }
    }
  ];

  return (
    <div className="portfolio-container">
      <div className="portfolio-header">
        <h1>Investment Strategies</h1>
        <p>
          Discover our premier funds designed to deliver exceptional returns through 
          innovative quantitative approaches and sophisticated risk management.
        </p>
      </div>

      <div className="funds-grid">
        {funds.map((fund, index) => (
          <FundCard key={index} fund={fund} />
        ))}
      </div>

      <PerformanceChart />

      <div className="cta-section">
        <h2 className="cta-title">Ready to Start Investing?</h2>
        <p className="cta-description">
          Join SwanPoint today and experience the future of quantitative investing 
          with our innovative fund strategies.
        </p>
        
        <div className="cta-buttons">
        <Link to="/contact" className="cta-button primary"> Schedule a meeting  </Link>
        <ChevronRight size={20} />
          <button className="cta-button secondary">
            Download Investment Guide
            <Download size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};
<Link to="/portfolio" className="cta-button">Explore Our Strategies </Link>
export default Portfolio;